/**
 * <gift-box-builder> Custom Element
 *
 * Lets the customer fill N slots with products from a collection (picked in a <dialog>). The gift box is
 * always included, the greeting card is optional (checkbox) and added as a regular, paid product. On add to cart, all items are added in one /cart/add.js request, each multiplied by
 * the chosen box quantity (identical picks are merged into one line).
 *
 * All lines of one box share the hidden "_Geschenkbox" property, so they stay grouped and separate from regular
 * purchases of the same products. After adding, the theme's "variant:add" / "cart:change" events are dispatched so the
 * cart drawer / cart count update exactly like with the regular product form.
 */

class GiftBoxBuilder extends HTMLElement {
  connectedCallback() {
    this.slotCount = parseInt(this.dataset.slotCount, 10) || 3;
    this.allowDuplicates = this.dataset.allowDuplicates === 'true';
    this.maxQuantity = parseInt(this.dataset.maxQuantity, 10) || 100;
    this.moneyFormat = this.dataset.moneyFormat || '{{amount_with_comma_separator}} €';
    this.boxVariantId = parseInt(this.dataset.boxVariantId, 10) || null;
    this.cardVariantId = parseInt(this.dataset.cardVariantId, 10) || null;
    this.boxPrice = parseInt(this.dataset.boxPrice, 10) || 0;
    this.cardPrice = parseInt(this.dataset.cardPrice, 10) || 0;

    const stringsEl = this.querySelector('[data-gift-box-strings]');
    this.strings = stringsEl ? JSON.parse(stringsEl.textContent) : {};

    this.selection = new Array(this.slotCount).fill(null);
    this.activeSlot = null;

    this.slots = Array.from(this.querySelectorAll('[data-gift-box-slot]'));
    this.options = Array.from(this.querySelectorAll('[data-gift-box-option]'));
    this.dialog = this.querySelector('[data-gift-box-dialog]');
    this.quantityInput = this.querySelector('[data-gift-box-quantity]');
    this.messageInput = this.querySelector('[data-gift-box-message]');
    this.messageWrapper = this.querySelector('[data-gift-box-message-wrapper]');
    this.cardToggle = this.querySelector('[data-gift-box-card-toggle]');
    this.atcButton = this.querySelector('[data-gift-box-atc]');
    this.atcLabel = this.querySelector('[data-gift-box-atc-label]');
    this.totalEl = this.querySelector('[data-gift-box-total]');
    this.totalHintEl = this.querySelector('[data-gift-box-total-hint]');
    this.errorEl = this.querySelector('[data-gift-box-error]');
    this.mainImage = this.querySelector('[data-gift-box-main-image]');

    this.slots.forEach((slot) => {
      slot.addEventListener('click', () => this.openPicker(parseInt(slot.dataset.giftBoxSlot, 10)));
    });

    this.options.forEach((option) => {
      option.addEventListener('click', () => this.selectOption(option));
    });

    this.querySelector('[data-gift-box-close]')?.addEventListener('click', () => this.dialog.close());
    this.dialog?.addEventListener('click', (event) => {
      // Close when clicking on the backdrop
      if (event.target === this.dialog) this.dialog.close();
    });

    this.querySelectorAll('[data-gift-box-qty]').forEach((button) => {
      button.addEventListener('click', () => {
        this.setQuantity(this.getQuantity() + parseInt(button.dataset.giftBoxQty, 10));
      });
    });
    this.quantityInput?.addEventListener('change', () => this.setQuantity(this.getQuantity()));

    // Greeting card is optional: only priced and added to cart when the checkbox is ticked
    this.cardToggle?.addEventListener('change', () => {
      if (this.messageWrapper) this.messageWrapper.hidden = !this.cardToggle.checked;
      this.render();
    });

    this.atcButton?.addEventListener('click', () => this.addToCart());

    this.querySelectorAll('[data-gift-box-thumb]').forEach((thumb, _, thumbs) => {
      thumb.addEventListener('click', () => {
        if (!this.mainImage) return;
        this.mainImage.removeAttribute('srcset');
        this.mainImage.src = thumb.dataset.src;
        thumbs.forEach((t) => t.setAttribute('aria-current', t === thumb ? 'true' : 'false'));
      });
    });

    this.render();
  }

  /* -------------------- Selection -------------------- */

  openPicker(slotIndex) {
    this.activeSlot = slotIndex;
    this.updateOptionStates();
    this.dialog?.showModal();
  }

  selectOption(option) {
    if (option.disabled || this.activeSlot === null) return;

    this.selection[this.activeSlot] = {
      variantId: parseInt(option.dataset.variantId, 10),
      price: parseInt(option.dataset.price, 10) || 0,
      title: option.dataset.title,
      image: option.dataset.image,
    };

    // Jump to the next empty slot so the picker flow feels natural
    const nextEmpty = this.selection.findIndex((item) => item === null);
    this.dialog.close();
    this.render();

    if (nextEmpty !== -1) {
      requestAnimationFrame(() => this.slots[nextEmpty]?.focus());
    }
  }

  // When duplicates are not allowed, disable options already used in another slot
  updateOptionStates() {
    if (this.allowDuplicates) return;

    const usedIds = this.selection
      .filter((item, index) => item && index !== this.activeSlot)
      .map((item) => item.variantId);

    this.options.forEach((option) => {
      if (option.dataset.soldOut === undefined) {
        option.dataset.soldOut = option.disabled ? 'true' : 'false';
      }
      const used = usedIds.includes(parseInt(option.dataset.variantId, 10));
      option.disabled = option.dataset.soldOut === 'true' || used;
    });
  }

  /* -------------------- Quantity -------------------- */

  getQuantity() {
    const value = parseInt(this.quantityInput?.value, 10);
    return Number.isNaN(value) ? 1 : value;
  }

  setQuantity(value) {
    const quantity = Math.min(Math.max(1, value), this.maxQuantity);
    if (this.quantityInput) this.quantityInput.value = quantity;
    this.render();
  }

  /* -------------------- Rendering -------------------- */

  render() {
    this.selection.forEach((item, index) => {
      const slot = this.slots[index];
      if (!slot) return;

      const imageEl = slot.querySelector('[data-slot-image]');
      const titleEl = slot.querySelector('[data-slot-title]');
      const metaEl = slot.querySelector('[data-slot-meta]');
      const actionEl = slot.querySelector('[data-slot-action]');

      if (!slot._placeholderImage) slot._placeholderImage = imageEl.innerHTML;

      if (item) {
        slot.dataset.filled = 'true';
        imageEl.innerHTML = item.image ? `<img src="${item.image}" alt="" loading="lazy">` : slot._placeholderImage;
        titleEl.textContent = item.title;
        metaEl.textContent = `${this.formatMoney(item.price)} · ${this.strings.change || ''}`;
        metaEl.hidden = false;
        actionEl.textContent = '↻';
      } else {
        slot.dataset.filled = 'false';
        imageEl.innerHTML = slot._placeholderImage;
        titleEl.textContent = `${this.strings.placeholder || ''} ${index + 1}`;
        metaEl.hidden = true;
        actionEl.textContent = '+';
      }
    });

    const quantity = this.getQuantity();

    this.querySelectorAll('[data-gift-box-qty]').forEach((button) => {
      const step = parseInt(button.dataset.giftBoxQty, 10);
      button.disabled = step < 0 ? quantity <= 1 : quantity >= this.maxQuantity;
    });

    const boxPrice = this.boxPrice + (this.isCardSelected() ? this.cardPrice : 0) + this.selection.reduce((sum, item) => sum + (item ? item.price : 0), 0);

    this.totalEl.textContent = this.formatMoney(boxPrice * quantity);
    this.totalHintEl.textContent = quantity > 1 ? (this.strings.perBox || '').replace('[price]', this.formatMoney(boxPrice)) : '';

    const missing = this.selection.filter((item) => item === null).length;
    this.atcButton.disabled = missing > 0;
    this.atcLabel.textContent = missing > 0
      ? (this.strings.incomplete || '').replace('[count]', missing)
      : this.strings.complete;
  }

  isCardSelected() {
    return Boolean(this.cardVariantId && this.cardToggle?.checked);
  }

  formatMoney(cents) {
    const format = (value, decimals, thousands, decimal) => {
      const [whole, fraction] = (value / 100).toFixed(decimals).split('.');
      return whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousands) + (fraction ? decimal + fraction : '');
    };

    return this.moneyFormat.replace(/\{\{\s*(\w+)\s*\}\}/, (_, placeholder) => {
      switch (placeholder) {
        case 'amount_no_decimals': return format(cents, 0, ',', '.');
        case 'amount_with_comma_separator': return format(cents, 2, '.', ',');
        case 'amount_no_decimals_with_comma_separator': return format(cents, 0, '.', ',');
        case 'amount_with_apostrophe_separator': return format(cents, 2, "'", '.');
        default: return format(cents, 2, ',', '.');
      }
    });
  }

  /* -------------------- Cart -------------------- */

  buildItems(quantity) {
    const boxId = `GB-${Date.now().toString(36).toUpperCase()}`;
    const properties = { _Geschenkbox: boxId };
    const merged = new Map();

    const add = (variantId, extraProperties = {}) => {
      if (!variantId) return;
      const key = `${variantId}|${JSON.stringify(extraProperties)}`;
      if (merged.has(key)) {
        merged.get(key).quantity += quantity;
      } else {
        merged.set(key, { id: variantId, quantity, properties: { ...properties, ...extraProperties } });
      }
    };

    this.selection.forEach((item) => add(item.variantId));

    if (this.isCardSelected()) {
      const message = this.messageInput?.value.trim();
      add(this.cardVariantId, message ? { 'Grußkarte': message } : {});
    }
    add(this.boxVariantId);

    return Array.from(merged.values());
  }

  async addToCart() {
    if (this.selection.some((item) => item === null)) return;

    this.errorEl.hidden = true;
    this.atcButton.setAttribute('aria-busy', 'true');
    this.atcButton.disabled = true;

    const sectionsToBundle = ['variant-added'];
    document.documentElement.dispatchEvent(new CustomEvent('cart:prepare-bundled-sections', {
      bubbles: true,
      detail: { sections: sectionsToBundle },
    }));

    try {
      const response = await fetch(`${Shopify.routes.root}cart/add.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: JSON.stringify({
          items: this.buildItems(this.getQuantity()),
          sections: sectionsToBundle.join(','),
          sections_url: `${Shopify.routes.root}variants/${this.boxVariantId || this.selection[0].variantId}`,
        }),
      });

      const responseJson = await response.json();

      if (!response.ok) {
        throw new Error(responseJson.description || responseJson.message || this.strings.error);
      }

      if (window.themeVariables?.settings?.cartType === 'page') {
        window.location.href = `${Shopify.routes.root}cart`;
        return;
      }

      const cartContent = await (await fetch(`${Shopify.routes.root}cart.js`)).json();
      cartContent.sections = responseJson.sections;

      this.dispatchEvent(new CustomEvent('variant:add', {
        bubbles: true,
        detail: { items: responseJson.items || [responseJson], cart: cartContent },
      }));

      document.documentElement.dispatchEvent(new CustomEvent('cart:change', {
        bubbles: true,
        detail: { baseEvent: 'variant:add', cart: cartContent },
      }));
    } catch (error) {
      this.errorEl.textContent = error.message || this.strings.error;
      this.errorEl.hidden = false;
      document.dispatchEvent(new CustomEvent('cart:refresh'));
    } finally {
      this.atcButton.removeAttribute('aria-busy');
      this.render();
    }
  }
}

if (!window.customElements.get('gift-box-builder')) {
  window.customElements.define('gift-box-builder', GiftBoxBuilder);
}
